'use client'

import clsx from 'clsx'
import Link from 'next/link'

import { programDetailScrollToElementWithCustomOffset } from '../../_utils'

/**
 * Props for the ZAPDetailButton component.
 */
interface IZAPDetailButtonProps {
	/** Button text label */
	text: string
	/** Optional click handler */
	onClick?: () => void
	/** Whether button uses primary or outline style */
	isPrimary: boolean
	/** Optional price to display */
	price?: number
	/** Optional old price to display with strikethrough */
	priceOld?: number
	/** Button color theme */
	color: 'blue' | 'cyclamen' | 'child'
	/** Optional ID of section to scroll to on click */
	scrollToId?: string
	/** Optional href for link button */
	href?: string
	/** Additional CSS classes */
	className?: string
}

/**
 * Displays price information with optional old price.
 *
 * @param price - Current price
 * @param priceOld - Optional old price to show as strikethrough
 * @returns Price display component or null if no price
 */
const ZAPDetailButtonPrice = ({ price, priceOld }: { price?: number; priceOld?: number }) => {
	if (!price) {
		return null
	}

	if (!priceOld) {
		return <span className='price'>({price}&nbsp;€)</span>
	}

	return (
		<span className='price'>
			({price}&nbsp;€<span className='price--old'>{priceOld}&nbsp;€</span>)
		</span>
	)
}

/**
 * Universal button component for annual program details.
 * Supports both button and link modes, with optional pricing display.
 * Can trigger scroll navigation or standard click handlers.
 * Shared across all annual program detail pages (Balance, Mini, Vital).
 *
 * @param text - Button label text
 * @param onClick - Optional click handler
 * @param isPrimary - Whether to use filled (true) or outline (false) style
 * @param color - Theme color ('blue' for male, 'cyclamen' for female)
 * @param price - Optional current price
 * @param priceOld - Optional old price for comparison
 * @param scrollToId - Optional section ID to scroll to on click
 * @param href - Optional href to render as link instead of button
 * @param className - Additional CSS classes
 *
 * @example
 * ```typescript
 * // Button with scroll
 * <ZAPDetailButton
 *   text='Medante Balance pre ženu'
 *   onClick={handleClick}
 *   isPrimary={true}
 *   color='cyclamen'
 *   scrollToId='detail-programu'
 * />
 *
 * // Link button with pricing
 * <ZAPDetailButton
 *   text='Objednať'
 *   href='/volne-terminy?id=123'
 *   isPrimary={true}
 *   color='blue'
 *   price={299}
 *   priceOld={399}
 * />
 * ```
 */
export default function ZAPDetailButton({
	text,
	onClick,
	isPrimary,
	color,
	price,
	priceOld,
	scrollToId,
	href,
	className
}: IZAPDetailButtonProps) {
	/**
	 * Handles button click events.
	 * If scrollToId is provided, scrolls to that element with offset.
	 * Also triggers the optional onClick handler.
	 */
	const handleClick = (): void => {
		if (scrollToId) {
			programDetailScrollToElementWithCustomOffset(scrollToId, 100)
		}
		if (onClick) {
			onClick()
		}
	}

	// Render as link if href is provided
	if (href) {
		return (
			<Link
				title={text}
				className={clsx(
					'btn_basic medante-vital__button',
					`${color}${isPrimary ? '' : '-outline'}`,
					className
				)}
				style={{ textDecoration: 'none' }}
				href={href}
				onClick={onClick}
			>
				<span className='text-center!'>
					{text}
					<ZAPDetailButtonPrice price={price} priceOld={priceOld} />
				</span>
			</Link>
		)
	}

	// Render as button
	return (
		<button
			onClick={handleClick}
			title={text}
			className={clsx(
				'btn_basic medante-vital__button cursor-pointer!',
				`${color}${isPrimary ? '' : '-outline'}`,
				className
			)}
			style={{ textDecoration: 'none' }}
		>
			<span className='text-center!'>
				{text}
				<ZAPDetailButtonPrice price={price} priceOld={priceOld} />
			</span>
		</button>
	)
}
